import { useState, type ReactNode } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import HelpTrigger from '../../../help/components/HelpTrigger';
import type { ButtonProps } from '@mui/material/Button';
import type { Theme } from '@mui/material/styles';

export type ReservationAction = {
  key: string;
  label: string;
  onClick: () => void;
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  icon?: ReactNode;
};

type Props = {
  actions: ReservationAction[];
  isBusy?: boolean;
};

export default function ReservationQuickActions({ actions, isBusy = false }: Props) {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (actions.length === 0) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        Aucune action immédiate
      </Typography>
    );
  }

  const primaryCount = isMobile ? 1 : 2;
  const primaryActions = actions.slice(0, primaryCount);
  const overflowActions = actions.slice(primaryCount);

  const hasValidateAction = actions.some((action) => action.key === 'validate-payment');
  const hasPartialCancelAction = actions.some((action) => action.key === 'cancel-booking-partial');
  const hasPartialRefundAction = actions.some((action) => action.key === 'refund-partial');
  const hasRefundAction = actions.some((action) => action.key === 'refund' || action.key === 'refuse-refund');
  const hasCancelAction = actions.some((action) => action.key === 'cancel-booking');

  const actionHelp = hasValidateAction
    ? { articleId: 'booking.action.validate_bank_transfer', category: 'booking.action', label: 'Aide sur la validation du virement' }
    : hasPartialRefundAction
      ? { articleId: 'booking.partial_refund.admin_processing', category: 'booking.partial_refund', label: 'Aide sur le remboursement partiel' }
    : hasRefundAction
      ? { articleId: 'booking.status.refund_pending', category: 'booking.status', label: 'Aide sur le remboursement à traiter' }
    : hasPartialCancelAction
      ? { articleId: 'booking.partial_refund.amounts', category: 'booking.partial_refund', label: 'Aide sur les montants après annulation partielle' }
      : hasCancelAction
        ? { articleId: 'booking.action.cancel_booking', category: 'booking.action', label: 'Aide sur l’annulation d’une réservation' }
        : null;

  return (
    <>
      <Stack
        direction="row"
        useFlexGap
        flexWrap="wrap"
        gap={1}
        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        sx={{ width: '100%' }}
      >
        {primaryActions.map((action, index) => (
          <Button
            key={action.key}
            size="small"
            color={action.color || 'primary'}
            variant={action.variant || 'contained'}
            onClick={action.onClick}
            disabled={isBusy}
            startIcon={isBusy && index === 0 ? <CircularProgress size={14} color="inherit" /> : action.icon}
            sx={{
              flexGrow: { xs: 1, md: 0 },
              minWidth: { xs: 'calc(50% - 4px)', md: 0 },
              whiteSpace: 'nowrap',
            }}
          >
            {action.label}
          </Button>
        ))}

        {actionHelp ? (
          <HelpTrigger
            articleId={actionHelp.articleId}
            category={actionHelp.category}
            label={actionHelp.label}
          />
        ) : null}

        {overflowActions.length > 0 ? (
          <IconButton
            aria-label="Plus d'actions"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            size="small"
            sx={{ border: '1px solid', borderColor: 'divider' }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {overflowActions.map((action) => (
          <MenuItem
            key={action.key}
            onClick={() => {
              setAnchorEl(null);
              action.onClick();
            }}
            disabled={isBusy}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
